import React, { createContext, useContext, useState, useEffect } from 'react';
import { useAuth } from './AuthContext';
import { dbOperations } from '../utils/supabase';

const CartContext = createContext();

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};

export const CartProvider = ({ children }) => {
  const [cartItems, setCartItems] = useState([]);
  const [isCartOpen, setIsCartOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const { user } = useAuth();

  // Kullanıcı değiştiğinde sepeti yükle
  useEffect(() => {
    if (user) {
      loadCartFromDatabase();
    } else {
      // Kullanıcı giriş yapmamışsa localStorage'dan yükle
      loadCartFromLocalStorage();
    }
  }, [user]);

  // Veritabanından sepeti yükle
  const loadCartFromDatabase = async () => {
    if (!user) return;
    
    try {
      setLoading(true);
      const cartData = await dbOperations.cart.getByUserId(user.id);
      
      // Veritabanı formatını UI formatına çevir
      const formattedItems = cartData.map(item => ({
        id: item.product_id,
        name: item.products.name,
        price: item.final_price,
        originalPrice: item.original_price,
        discount: item.discount,
        image: item.products.image_url,
        quantity: item.quantity,
        options: item.options,
        isSet: item.is_set,
        setItems: item.set_items,
        category: item.products.category,
        addedAt: item.created_at,
        cartItemId: item.id // Database primary key
      }));
      
      setCartItems(formattedItems);
    } catch (error) {
      console.error('Error loading cart from database:', error);
      // Hata durumunda localStorage'dan yükle
      loadCartFromLocalStorage();
    } finally {
      setLoading(false);
    }
  };

  // LocalStorage'dan sepeti yükle
  const loadCartFromLocalStorage = () => {
    const savedCart = localStorage.getItem('luxuryFurnitureCart');
    if (savedCart) {
      try {
        setCartItems(JSON.parse(savedCart));
      } catch (error) {
        console.error('Error loading cart from localStorage:', error);
        setCartItems([]);
      }
    }
  };

  // Sepet değiştiğinde localStorage'a kaydet (misafir kullanıcılar için)
  useEffect(() => {
    if (!user) {
      localStorage.setItem('luxuryFurnitureCart', JSON.stringify(cartItems));
    }
  }, [cartItems, user]);

  const addToCart = async (product, quantity = 1, options = {}) => {
    if (user) {
      // Giriş yapmış kullanıcı - veritabanına kaydet
      try {
        await dbOperations.cart.addOrUpdate(user.id, {
          productId: product.id,
          quantity,
          options,
          isSet: product.isSet || false,
          setItems: product.setItems || null,
          originalPrice: product.originalPrice || product.price,
          discount: product.discount || 0,
          finalPrice: product.price
        });
        
        // Sepeti yeniden yükle
        await loadCartFromDatabase();
      } catch (error) {
        console.error('Error adding to cart:', error);
        // Hata durumunda local state'i güncelle
        addToCartLocal(product, quantity, options);
      }
    } else {
      // Misafir kullanıcı - local state'i güncelle
      addToCartLocal(product, quantity, options);
    }
  };

  const addToCartLocal = (product, quantity = 1, options = {}) => {
    setCartItems(prevItems => {
      const existingItemIndex = prevItems.findIndex(
        item => item.id === product.id && 
                JSON.stringify(item.options) === JSON.stringify(options)
      );

      if (existingItemIndex >= 0) {
        const updatedItems = [...prevItems];
        updatedItems[existingItemIndex].quantity += quantity;
        return updatedItems;
      } else {
        return [...prevItems, {
          ...product,
          quantity,
          options,
          cartItemId: Date.now() + Math.random(), // Unique cart item ID
        }];
      }
    });
  };

  const removeFromCart = async (cartItemId) => {
    if (user) {
      try {
        // cartItemId veritabanı ID'si ise direkt sil
        if (typeof cartItemId === 'string' && cartItemId.includes('-')) {
          await dbOperations.cart.remove(user.id, cartItemId);
        } else {
          // cartItemId product ID'si ise product ID ile sil
          const item = cartItems.find(item => item.cartItemId === cartItemId);
          if (item) {
            await dbOperations.cart.remove(user.id, item.id);
          }
        }
        await loadCartFromDatabase();
      } catch (error) {
        console.error('Error removing from cart:', error);
        // Hata durumunda local state'i güncelle
        setCartItems(prevItems => 
          prevItems.filter(item => item.cartItemId !== cartItemId)
        );
      }
    } else {
      setCartItems(prevItems => 
        prevItems.filter(item => item.cartItemId !== cartItemId)
      );
    }
  };

  const updateQuantity = async (cartItemId, newQuantity) => {
    if (newQuantity <= 0) {
      await removeFromCart(cartItemId);
      return;
    }

    if (user) {
      try {
        const item = cartItems.find(item => item.cartItemId === cartItemId);
        if (item) {
          await dbOperations.cart.updateQuantity(user.id, item.id, newQuantity);
          await loadCartFromDatabase();
        }
      } catch (error) {
        console.error('Error updating quantity:', error);
        // Hata durumunda local state'i güncelle
        setCartItems(prevItems =>
          prevItems.map(item =>
            item.cartItemId === cartItemId
              ? { ...item, quantity: newQuantity }
              : item
          )
        );
      }
    } else {
      setCartItems(prevItems =>
        prevItems.map(item =>
          item.cartItemId === cartItemId
            ? { ...item, quantity: newQuantity }
            : item
        )
      );
    }
  };

  const clearCart = async () => {
    if (user) {
      try {
        await dbOperations.cart.clear(user.id);
        setCartItems([]);
      } catch (error) {
        console.error('Error clearing cart:', error);
        setCartItems([]);
      }
    } else {
      setCartItems([]);
    }
  };

  const getCartTotal = () => {
    return cartItems.reduce((total, item) => {
      const price = item.salePrice || item.price;
      return total + (price * item.quantity);
    }, 0);
  };

  const getCartItemsCount = () => {
    return cartItems.reduce((count, item) => count + item.quantity, 0);
  };

  const toggleCart = () => {
    setIsCartOpen(!isCartOpen);
  };

  const closeCart = () => {
    setIsCartOpen(false);
  };

  const openCart = () => {
    setIsCartOpen(true);
  };

  // Misafir sepetini kullanıcı girişinde veritabanına aktar
  const migrateGuestCart = async () => {
    if (!user || cartItems.length === 0) return;

    try {
      for (const item of cartItems) {
        await dbOperations.cart.addOrUpdate(user.id, {
          productId: item.id,
          quantity: item.quantity,
          options: item.options,
          isSet: item.isSet || false,
          setItems: item.setItems || null,
          originalPrice: item.originalPrice || item.price,
          discount: item.discount || 0,
          finalPrice: item.price
        });
      }
      
      // localStorage'ı temizle
      localStorage.removeItem('luxuryFurnitureCart');
      
      // Veritabanından güncel sepeti yükle
      await loadCartFromDatabase();
    } catch (error) {
      console.error('Error migrating guest cart:', error);
    }
  };

  // Kullanıcı giriş yaptığında misafir sepetini aktar
  useEffect(() => {
    if (user && cartItems.length > 0 && !loading) {
      migrateGuestCart();
    }
  }, [user]);

  const value = {
    cartItems,
    isCartOpen,
    loading,
    addToCart,
    removeFromCart,
    updateQuantity,
    clearCart,
    getCartTotal,
    getCartItemsCount,
    toggleCart,
    closeCart,
    openCart,
    loadCartFromDatabase
  };

  return (
    <CartContext.Provider value={value}>
      {children}
    </CartContext.Provider>
  );
}; 